import { useNavigation } from "@-/com.npmjs.expo-router";
import { useCallback, useEffect, useState } from "react";

type NavigationLike = {
  addListener: (type: "state", listener: () => void) => () => void;
  getState: () => { index: number } | undefined;
  goBack: () => void;
};

function isNavigationLike(value: unknown): value is NavigationLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "getState" in value &&
    typeof value.getState === "function" &&
    "goBack" in value &&
    typeof value.goBack === "function" &&
    "addListener" in value &&
    typeof value.addListener === "function"
  );
}

function readIndex(navigation: NavigationLike | undefined): number {
  return navigation?.getState()?.index ?? 0;
}

/**
 * Back handler scoped to the screen's IMMEDIATE parent navigator: returns a `goBack` callback
 * only when that navigator itself has a previous entry to pop (`index > 0`), and
 * `undefined` otherwise.
 *
 * This is for in-content back chevrons (the morpheme-panels toolbar, the review card) whose
 * "back" is meaningful only as "pop THIS stack" — e.g. after a panel→panel push such as tapping
 * a similar/component grapheme. `router.canGoBack()` is deliberately not used: it walks the
 * whole navigation tree, so unrelated ancestor history would make it report `true` and the
 * dispatched GO_BACK could pop a navigator the chevron never meant to touch.
 *
 * The navigator's index is SUBSCRIBED to, not sampled once at render. An earlier version read it
 * without subscribing on the reasoning that "the stack is fixed for the screen's lifetime, since a
 * push re-renders the screen anyway" — which is false. A push adds a sibling route; it does not
 * necessarily re-render the screen already mounted below it, and on the drill screen a reveal
 * pushes onto exactly this stack. The chevron then kept rendering the visibility it computed at
 * first mount, appearing on screens with nothing to pop and missing on screens that had history.
 */
export function useImmediateNavigatorOnBack(): (() => void) | undefined {
  const navigation = useNavigation();
  const navigationLike = isNavigationLike(navigation) ? navigation : undefined;
  const [index, setIndex] = useState(() => readIndex(navigationLike));

  useEffect(() => {
    if (navigationLike === undefined) {
      return;
    }
    // Re-sync on subscribe: the stack can have changed between the render that seeded state and
    // this effect running.
    setIndex(readIndex(navigationLike));
    return navigationLike.addListener("state", () => {
      setIndex(readIndex(navigationLike));
    });
  }, [navigationLike]);

  const goBack = useCallback(() => {
    navigationLike?.goBack();
  }, [navigationLike]);

  if (navigationLike === undefined) {
    return undefined;
  }
  return index > 0 ? goBack : undefined;
}
